import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch, parseJsonSafe, handleUnauthorized } from '../utils/api';

// «Presupuesto en 1 minuto»: tarifas (desde / hasta) del cuestionario de las landings de campaña,
// configuración de la conexión con la web y últimas peticiones recibidas.

const LANDING_TABS = { software: 'Software a medida', ia: 'IA para empresas', tienda: 'Tienda online con IA', apps: 'Apps móviles' };

const STATUS = {
  enviado: { label: 'Enviado', cls: 'bg-success' },
  sin_email: { label: 'Sin enviar', cls: 'bg-danger' },
  recibido: { label: 'En curso', cls: 'bg-warning text-dark' }
};

const money = (n) => {
  if (n === null || n === undefined || n === '') return '-';
  return `${String(Math.round(Number(n) || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, '.')} €`;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
};

const QuickQuotePage = () => {
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const [data, setData] = useState(null);
  const [tab, setTab] = useState('software');
  const [draft, setDraft] = useState({});        // { landing: { key: { min, max } } }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [showSecret, setShowSecret] = useState(false);

  const call = useCallback(async (path, options = {}) => {
    const res = await apiFetch(`/api/quick-quote${path}`, { ...options, headers });
    if (res.status === 401) { handleUnauthorized(); throw new Error('Sesión caducada'); }
    const body = await parseJsonSafe(res);
    if (!res.ok) throw new Error(body.message || body.error || 'Error');
    return body;
  }, [headers]);

  const draftFrom = (landings) => {
    const out = {};
    (landings || []).forEach((l) => {
      out[l.landing] = {};
      l.items.forEach((it) => { out[l.landing][it.key] = { min: String(it.min), max: String(it.max) }; });
    });
    return out;
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const body = await call('/admin');
      setData(body);
      setDraft(draftFrom(body.landings));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [call]);

  useEffect(() => { load(); }, [load]);

  const landing = (data?.landings || []).find((l) => l.landing === tab);

  const rowError = (it) => {
    const d = draft[tab]?.[it.key];
    if (!d) return '';
    const min = Number(d.min);
    const max = Number(d.max);
    if (d.min === '' || d.max === '' || !Number.isFinite(min) || !Number.isFinite(max) || min < 0 || max < 0) return 'Importe no válido';
    if (max < min) return 'El «hasta» es menor que el «desde»';
    return '';
  };

  const dirty = landing ? landing.items.some((it) => {
    const d = draft[tab]?.[it.key];
    return d && (Number(d.min) !== it.min || Number(d.max) !== it.max || d.min === '' || d.max === '');
  }) : false;
  const hasErrors = landing ? landing.items.some((it) => rowError(it)) : false;

  const setValue = (key, field, value) => {
    setDraft((p) => ({ ...p, [tab]: { ...(p[tab] || {}), [key]: { ...(p[tab]?.[key] || {}), [field]: value } } }));
  };

  const resetToDefaults = () => {
    if (!landing) return;
    setDraft((p) => ({
      ...p,
      [tab]: Object.fromEntries(landing.items.map((it) => [it.key, { min: String(it.defaultMin), max: String(it.defaultMax) }]))
    }));
    setNotice('Importes del borrador cargados. Pulsa «Guardar tarifas» para aplicarlos.');
  };

  const save = async () => {
    if (!landing || hasErrors) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const items = landing.items.map((it) => ({ key: it.key, min: Number(draft[tab][it.key].min), max: Number(draft[tab][it.key].max) }));
      const body = await call(`/admin/tariffs/${tab}`, { method: 'PUT', body: JSON.stringify({ items }) });
      setData((p) => ({ ...p, landings: body.landings }));
      setDraft(draftFrom(body.landings));
      setNotice(`Tarifas de «${LANDING_TABS[tab]}» guardadas. Los próximos presupuestos ya salen con estos importes.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveConfig = async (patch, okMsg) => {
    setError('');
    setNotice('');
    try {
      const body = await call('/admin/config', { method: 'PUT', body: JSON.stringify(patch) });
      setData((p) => ({ ...p, config: body.config }));
      if (okMsg) setNotice(okMsg);
    } catch (err) {
      setError(err.message);
    }
  };

  const regenerate = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Si cambias la clave, el cuestionario de la web dejará de funcionar hasta que pongas la nueva en el servidor de la web. ¿Seguro?')) return;
    saveConfig({ regenerateSecret: true }, 'Clave nueva generada. Cámbiala también en el servidor de la web.');
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(data?.config?.secret || '');
      setNotice('Clave copiada.');
    } catch (_e) {
      setShowSecret(true);
    }
  };

  if (loading && !data) return <div className="container-fluid"><p className="text-muted">Cargando…</p></div>;

  return (
    <div className="container-fluid">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h2 className="mb-1">Presupuesto en 1 minuto</h2>
          <div className="text-muted small">
            Cuestionario de las landings de campaña. Cada lead recibe al momento un PDF orientativo con el «desde» y el «hasta»;
            aquí se crea el lead y un presupuesto de la serie PO con el «desde» como precio.
          </div>
        </div>
        {data?.config && (
          <div className="form-check form-switch mt-1">
            <input
              className="form-check-input"
              type="checkbox"
              id="qq-enabled"
              checked={data.config.enabled}
              onChange={(e) => saveConfig({ enabled: e.target.checked }, e.target.checked ? 'Cuestionario activado.' : 'Cuestionario desactivado: la web avisará de llamar por teléfono.')}
            />
            <label className="form-check-label" htmlFor="qq-enabled">{data.config.enabled ? 'Activo' : 'Desactivado'}</label>
          </div>
        )}
      </div>

      {error && <div className="alert alert-danger py-2" role="alert">{error} <button type="button" className="btn-close float-end" aria-label="Cerrar" onClick={() => setError('')} /></div>}
      {notice && <div className="alert alert-success py-2" role="status">{notice} <button type="button" className="btn-close float-end" aria-label="Cerrar" onClick={() => setNotice('')} /></div>}

      <div className="card mb-4">
        <div className="card-header bg-white">
          <ul className="nav nav-tabs card-header-tabs">
            {Object.entries(LANDING_TABS).map(([key, label]) => (
              <li className="nav-item" key={key}>
                <button type="button" className={`nav-link ${tab === key ? 'active' : ''}`} onClick={() => { setTab(key); setNotice(''); }}>{label}</button>
              </li>
            ))}
          </ul>
        </div>
        <div className="card-body">
          {landing && (
            <>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <div className="small text-muted">
                  Landing: <a href={`https://inter-conecta.com${landing.path}`} target="_blank" rel="noreferrer">inter-conecta.com{landing.path}</a> · Importes sin IVA.
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetToDefaults}>Volver a los importes del borrador</button>
                  <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={!dirty || hasErrors || saving}>
                    {saving ? 'Guardando…' : 'Guardar tarifas'}
                  </button>
                </div>
              </div>
              {/* Móvil: una tarjeta por concepto con los dos importes a lo ancho (en la tabla los
                  campos quedaban tan estrechos que no se veía ni la cifra) */}
              <div className="d-md-none mb-3">
                {landing.items.map((it) => {
                  const err = rowError(it);
                  return (
                    <div key={it.key} className="border rounded p-2 mb-2">
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div className="fw-semibold">{it.concepto}</div>
                        <span className={`badge flex-shrink-0 ${it.origen === 'web' ? 'bg-success-subtle text-success-emphasis' : 'bg-warning-subtle text-warning-emphasis'}`}>
                          {it.origen === 'web' ? 'Desde tuyo' : 'Propuesta'}
                        </span>
                      </div>
                      <div className="row g-2 mt-1">
                        {['min', 'max'].map((field) => (
                          <div className="col-6" key={field}>
                            <label className="form-label small text-muted mb-1">{field === 'min' ? 'Desde' : 'Hasta'}</label>
                            <div className="input-group">
                              <input
                                type="number"
                                inputMode="decimal"
                                min="0"
                                step="10"
                                className={`form-control text-end ${err ? 'is-invalid' : ''}`}
                                value={draft[tab]?.[it.key]?.[field] ?? ''}
                                onChange={(e) => setValue(it.key, field, e.target.value)}
                                aria-label={`${field === 'min' ? 'Desde' : 'Hasta'}: ${it.concepto}`}
                              />
                              <span className="input-group-text">€</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      {err && <div className="small text-danger mt-1">{err}</div>}
                    </div>
                  );
                })}
              </div>
              <div className="table-responsive d-none d-md-block">
                <table className="table table-sm align-middle mb-3">
                  <thead>
                    <tr>
                      <th>Concepto</th>
                      <th style={{ width: 150 }}>Desde</th>
                      <th style={{ width: 150 }}>Hasta</th>
                      <th style={{ width: 120 }}>Origen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {landing.items.map((it) => {
                      const err = rowError(it);
                      return (
                        <tr key={it.key}>
                          <td>
                            {it.concepto}
                            {err && <div className="small text-danger">{err}</div>}
                          </td>
                          {['min', 'max'].map((field) => (
                            <td key={field}>
                              <div className="input-group input-group-sm" style={{ minWidth: 120 }}>
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  min="0"
                                  step="10"
                                  className={`form-control text-end ${err ? 'is-invalid' : ''}`}
                                  value={draft[tab]?.[it.key]?.[field] ?? ''}
                                  onChange={(e) => setValue(it.key, field, e.target.value)}
                                  aria-label={`${field === 'min' ? 'Desde' : 'Hasta'}: ${it.concepto}`}
                                />
                                <span className="input-group-text">€</span>
                              </div>
                            </td>
                          ))}
                          <td>
                            <span className={`badge ${it.origen === 'web' ? 'bg-success-subtle text-success-emphasis' : 'bg-warning-subtle text-warning-emphasis'}`}>
                              {it.origen === 'web' ? 'Desde tuyo' : 'Propuesta'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="small">
                <div className="fw-semibold mb-1">Ajustes que aplica el cálculo</div>
                <ul className="text-muted mb-0 ps-3">
                  {landing.ajustes.map((a) => <li key={a}>{a}</li>)}
                </ul>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header bg-white fw-semibold">Últimas peticiones</div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Landing</th>
                  <th>Lead</th>
                  <th className="text-end">Desde</th>
                  <th className="text-end">Hasta</th>
                  <th>Presupuesto</th>
                  <th>Origen</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {!(data?.requests || []).length && (
                  <tr><td colSpan={8} className="text-muted small p-3">Todavía no ha llegado ninguna petición.</td></tr>
                )}
                {(data?.requests || []).map((r) => {
                  const st = STATUS[r.status] || { label: r.status, cls: 'bg-secondary' };
                  return (
                    <tr key={r.id}>
                      <td className="small text-nowrap">{formatDateTime(r.created_at)}</td>
                      <td className="small">{r.service}</td>
                      <td className="small">
                        {r.client_id ? <Link to={`/clientes/${r.client_id}`}>{r.name}</Link> : r.name}
                        <div className="text-muted">{r.email}{r.phone ? ` · ${r.phone}` : ''}</div>
                      </td>
                      <td className="small text-end text-nowrap">{r.none ? '—' : money(r.total?.desde)}</td>
                      <td className="small text-end text-nowrap">{r.none ? '—' : money(r.total?.hasta)}</td>
                      <td className="small">
                        {r.budget_id ? <Link to={`/presupuestos/${r.budget_id}`} title="Abrir presupuesto generado">{r.ref || `Abrir presupuesto #${r.budget_id}`}</Link> : (r.none ? <span className="text-muted">Otra plataforma</span> : '-')}
                      </td>
                      <td className="small">{[r.utm_source, r.utm_campaign].filter(Boolean).join(' · ') || '-'}</td>
                      <td className="small">
                        <details><summary><span className={`badge ${st.cls}`}>{st.label}</span></summary><div className="small mt-2" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{r.error || (r.status === 'enviado' ? 'Email enviado correctamente.' : `Estado del envío: ${st.label}`)}</div></details>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {data?.config && (
        <div className="card mb-4">
          <div className="card-header bg-white fw-semibold">Envío y conexión con la web</div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-5">
                <label className="form-label small fw-semibold" htmlFor="qq-account">Cuenta desde la que se envía el email al lead</label>
                <select
                  id="qq-account"
                  className="form-select form-select-sm"
                  value={data.config.emailAccountId || ''}
                  onChange={(e) => saveConfig({ emailAccountId: e.target.value ? Number(e.target.value) : null }, 'Cuenta de envío guardada.')}
                >
                  <option value="">La primera cuenta activa</option>
                  {(data.accounts || []).map((a) => <option key={a.id} value={a.id}>{a.from_name ? `${a.from_name} <${a.from_email}>` : a.from_email}</option>)}
                </select>
              </div>
              <div className="col-md-7">
                <div className="form-label small fw-semibold">Clave de la web</div>
                {data.config.secretFromEnv ? (
                  <div className="small text-muted">La clave está en la variable de entorno QUICK_QUOTE_SECRET del servidor.</div>
                ) : (
                  <>
                    <div className="input-group input-group-sm">
                      <input className="form-control font-monospace" readOnly value={showSecret ? data.config.secret : '•'.repeat(32)} aria-label="Clave de la web" />
                      <button type="button" className="btn btn-outline-secondary" onClick={() => setShowSecret((v) => !v)}>{showSecret ? 'Ocultar' : 'Ver'}</button>
                      <button type="button" className="btn btn-outline-secondary" onClick={copySecret}>Copiar</button>
                      <button type="button" className="btn btn-outline-danger" onClick={regenerate}>Cambiar</button>
                    </div>
                    <div className="small text-muted mt-1">
                      Va en el servidor de la web, en el archivo <code>private/presupuesto-1-minuto.key</code> de inter-conecta.com (fuera de public_html).
                      Sin ella, la web no puede crear los presupuestos y solo avisa por email a info@.
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickQuotePage;
